import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

export interface Product {
  id: string;
  name: string;
  brand: string;
  category: string;
  batchId: string;
  productionDate: string;
  alcoholContent?: number;
  volumeMl?: number;
  description?: string;
  kraStampId?: string;
  kebsCertificationId?: string;
  createdAt: string;
  auth_tokens?: Array<{
    id: string;
    public_part: string;
    qr_code_url: string;
    is_burned: boolean;
  }>;
}

export interface VerificationResult {
  isAuthentic: boolean;
  isCounterfeit: boolean;
  product?: {
    name: string;
    brand: string;
    batchId: string;
    productionDate: string;
    alcoholContent?: number;
    volumeMl?: number;
    description?: string;
  };
  manufacturer?: {
    name: string;
    licenseNumber: string;
  };
  reason?: string;
  verificationTime?: string;
}

// Auth API
export const authAPI = {
  register: (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role?: string;
  }) => api.post('/auth/register', userData),

  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  getProfile: () => api.get('/auth/profile'),

  logout: () => api.post('/auth/logout'),
};

// Products API
export const productsAPI = {
  create: (productData: {
    name: string;
    brand: string;
    category: string;
    alcoholContent?: number;
    volumeMl?: number;
    description?: string;
    batchId: string;
    productionDate: string;
    expiryDate?: string;
    kraStampId?: string;
    kebsCertificationId?: string;
  }) => api.post('/products', productData),

  getAll: () => api.get('/products'),

  getById: (id: string) => api.get(`/products/${id}`),
};

// Verification API
export const verificationAPI = {
  verify: (publicToken: string, location?: { lat: number; lng: number }) =>
    api.post(`/verification/verify/${publicToken}`, { location }),

  getHistory: () => api.get('/verification/history'),

  reportCounterfeit: (reportData: {
    productName: string;
    suspectedManufacturer?: string;
    description: string;
    evidenceUrls?: string[];
    location?: { lat: number; lng: number };
  }) => api.post('/verification/report-counterfeit', reportData),
};

export default api;
